import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-rainy-line',
    title: 'Weather Resistant',
    description: 'Plastic totes protect belongings from rain and snow.',
  },
  {
    icon: 'ri-stack-line',
    title: 'Strong Stackable Design',
    description: "Totes won't collapse under weight like cardboard boxes.",
  },
  {
    icon: 'ri-truck-line',
    title: 'Perfect For Trailer Moves',
    description: 'Even when moving with an open trailer, items stay protected.',
  },
  {
    icon: 'ri-lock-2-line',
    title: 'Secure Lids',
    description: 'Keeps belongings dry during transport.',
  },
];

export default function WeatherProtection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div
          className="text-center mb-16 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)' }}
        >
          <span className="inline-block bg-yellow-100 text-yellow-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
            Built for Real Conditions
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-5 leading-tight">
            Rain Happens. Snow Happens.<br />
            <span className="text-yellow-400">Cardboard Boxes Don't Like Either.</span>
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Moving day doesn't always come with perfect weather. Cardboard boxes can quickly fall apart when they get wet — but reusable moving totes don't.
          </p>
        </div>

        {/* Two-column images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* Left: Totes in truck */}
          <div
            className="transition-all duration-700 delay-100"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-32px)' }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-lg group">
              <div className="w-full h-72">
                <img
                  src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/43c20aa881183167d34970a156f329a4.png"
                  alt="Moving truck neatly packed with black totes and yellow lids"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-yellow-400 rounded-lg">
                    <i className="ri-checkbox-circle-fill text-gray-900 text-lg" />
                  </div>
                  <span className="text-white font-bold text-sm">BoxFreeMove Totes — Weatherproof &amp; Ready</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Wet cardboard boxes */}
          <div
            className="transition-all duration-700 delay-200"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(32px)' }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-lg group">
              <div className="w-full h-72">
                <img
                  src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/8481195c0a33eaf57787f24e76d8a29c.png"
                  alt="Wet cardboard boxes collapsing in rain"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-500 rounded-lg">
                    <i className="ri-close-circle-fill text-white text-lg" />
                  </div>
                  <span className="text-white font-bold text-sm">Cardboard — Falls Apart When Wet</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Explanatory text */}
        <div
          className="max-w-3xl mx-auto text-center mb-14 transition-all duration-700 delay-300"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <p className="text-gray-600 text-base leading-relaxed">
            When cardboard gets wet, it loses structural integrity fast — bottoms give out, sides buckle, and your belongings are left exposed. Our heavy-duty plastic totes are built to handle the elements. Whether it's a drizzle on delivery day or a full snowstorm during your move, your items stay sealed, dry, and protected from start to finish.
          </p>
        </div>

        {/* Feature benefits grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(28px)',
                transitionDelay: `${300 + index * 100}ms`,
                transitionProperty: 'opacity, transform, box-shadow',
                transitionDuration: '0.6s',
              }}
            >
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-xl mb-4">
                <i className={`${feature.icon} text-2xl text-yellow-600`} />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Bold callout quote */}
        <div
          className="transition-all duration-700 delay-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <div className="relative bg-gray-900 rounded-2xl px-10 py-12 text-center overflow-hidden">
            {/* Decorative rain drops */}
            <div className="absolute top-4 left-8 w-6 h-6 flex items-center justify-center opacity-20">
              <i className="ri-drop-line text-yellow-400 text-3xl" />
            </div>
            <div className="absolute top-8 right-12 w-6 h-6 flex items-center justify-center opacity-20">
              <i className="ri-drop-line text-yellow-400 text-2xl" />
            </div>
            <div className="absolute bottom-6 left-16 w-6 h-6 flex items-center justify-center opacity-20">
              <i className="ri-drop-line text-yellow-400 text-xl" />
            </div>
            <div className="absolute bottom-4 right-8 w-6 h-6 flex items-center justify-center opacity-20">
              <i className="ri-drop-line text-yellow-400 text-3xl" />
            </div>

            <div className="w-14 h-14 flex items-center justify-center bg-yellow-400 rounded-full mx-auto mb-6">
              <i className="ri-shield-check-line text-gray-900 text-3xl" />
            </div>
            <p className="text-3xl md:text-4xl font-extrabold text-white leading-snug">
              Cardboard gets soggy.
            </p>
            <p className="text-3xl md:text-4xl font-extrabold text-yellow-400 mt-1 leading-snug">
              Totes stay strong.
            </p>
            <p className="text-gray-400 mt-5 text-base max-w-md mx-auto">
              Don't let the weather ruin your move. Our totes are built to handle whatever moving day throws at them.
            </p>
            <a
              href="#booking"
              className="inline-block mt-7 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold px-8 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Reserve Your Totes
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
